import { Suspense, lazy, type ComponentType } from 'react'
import { Outlet, createBrowserRouter } from 'react-router-dom'

import { AuthProvider, ProtectedRoute } from '@/components/auth-context'
import { AUTH_DISABLED } from '@/lib/auth-config'
import {
  DashboardRouteSkeleton,
  PageSectionSkeleton,
  ResultsPageSkeleton,
} from '@/components/loading-skeletons'
import { RecommendationGenerationLoading } from '@/components/recommendation/recommendation-generation-loading'
import HomePage from '@/src/pages/site/HomePage'
import NotFoundPage from '@/src/pages/site/NotFoundPage'
import LoginPage from '@/src/pages/site/auth/LoginPage'
import RegisterPage from '@/src/pages/site/auth/RegisterPage'
import SiteLayout from '@/src/pages/site/SiteLayout'
import RecommendationLoadingPreviewPage from '@/src/pages/site/recommendation/RecommendationLoadingPreviewPage'

const CalendarPage = lazy(() => import('@/src/pages/site/CalendarPage'))
const CeshiPage = lazy(() => import('@/src/pages/site/CeshiPage'))
const NotificationImportPage = lazy(() => import('@/src/pages/site/NotificationImportPage'))
const PricePredictionPage = lazy(() => import('@/src/pages/site/PricePredictionPage'))
const YieldPredictionPage = lazy(
  () => import('@/src/pages/site/yield-prediction/YieldPredictionPage')
)
const YieldPredictionResultsPage = lazy(
  () => import('@/src/pages/site/yield-prediction/YieldPredictionResultsPage')
)
const RecommendationResultsPage = lazy(
  () => import('@/src/pages/site/recommendation/RecommendationResultsPage')
)
const PricePredictionResultsPage = lazy(
  () => import('@/src/pages/site/price-prediction/PricePredictionResultsPage')
)
const CalendarResultsPage = lazy(() => import('@/src/pages/site/calendar/CalendarResultsPage'))
const LogoutPage = lazy(() => import('@/src/pages/site/auth/LogoutPage'))
const RecommendationContentPage = lazy(
  () => import('@/src/pages/site/recommendation/CropRecommendationContent')
)
const DiseaseDetectionContentPage = lazy(
  () => import('@/src/pages/site/disease-detection/DiseaseDetectionContent')
)
const DiseaseDetectionResultsPage = lazy(
  () => import('@/src/pages/site/disease-detection/DiseaseDetectionResultsPage')
)

const DashboardLayout = lazy(() => import('@/src/pages/dashboard/DashboardLayout'))
const DashboardPage = lazy(() => import('@/src/pages/dashboard/DashboardOverviewPage'))
const DashboardNotificationCenterPage = lazy(
  () => import('@/src/pages/dashboard/NotificationCenterPage')
)
const DashboardSearchManagementPage = lazy(
  () => import('@/src/pages/dashboard/SearchManagementPage')
)
const DashboardSettingsPage = lazy(() => import('@/src/pages/dashboard/SystemSettingsPage'))
const DashboardUserManagementPage = lazy(() => import('@/src/pages/dashboard/UserManagementPage'))
const DashboardSettingsSimplePage = lazy(() => import('@/src/pages/dashboard/SettingsPage'))

const PAGE_LOADING_FALLBACK = <PageSectionSkeleton />
const RESULTS_LOADING_FALLBACK = <ResultsPageSkeleton />
const RECOMMENDATION_LOADING_FALLBACK = <RecommendationGenerationLoading />
const DASHBOARD_LOADING_FALLBACK = <DashboardRouteSkeleton />

function withLayout(Component: ComponentType) {
  return (
    <Suspense fallback={PAGE_LOADING_FALLBACK}>
      <Component />
    </Suspense>
  )
}

function AuthShell() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  )
}

function RecommendationProtectedRoute() {
  return (
    <Suspense fallback={PAGE_LOADING_FALLBACK}>
      {AUTH_DISABLED ? (
        <RecommendationContentPage />
      ) : (
        <ProtectedRoute>
          <RecommendationContentPage />
        </ProtectedRoute>
      )}
    </Suspense>
  )
}

function DiseaseDetectionProtectedRoute() {
  return (
    <Suspense fallback={PAGE_LOADING_FALLBACK}>
      {AUTH_DISABLED ? (
        <DiseaseDetectionContentPage />
      ) : (
        <ProtectedRoute>
          <DiseaseDetectionContentPage />
        </ProtectedRoute>
      )}
    </Suspense>
  )
}

function SiteShell() {
  return (
    <SiteLayout>
      <Outlet />
    </SiteLayout>
  )
}

function DashboardShell() {
  return (
    <Suspense fallback={DASHBOARD_LOADING_FALLBACK}>
      <ProtectedRoute>
        <DashboardLayout>
          <Outlet />
        </DashboardLayout>
      </ProtectedRoute>
    </Suspense>
  )
}

export const appRouter = createBrowserRouter([
  {
    element: <AuthShell />,
    children: [
      {
        path: '/',
        element: <SiteShell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'calendar', element: withLayout(CalendarPage) },
          { path: 'ceshi', element: withLayout(CeshiPage) },
          { path: 'disease-detection', element: <DiseaseDetectionProtectedRoute /> },
          {
            path: 'disease-detection/results',
            element: (
              <Suspense fallback={RESULTS_LOADING_FALLBACK}>
                <DiseaseDetectionResultsPage />
              </Suspense>
            ),
          },
          { path: 'login', element: <LoginPage /> },
          { path: 'logout', element: <LogoutPage /> },
          { path: 'notification-import', element: withLayout(NotificationImportPage) },
          { path: 'price-prediction', element: withLayout(PricePredictionPage) },
          {
            path: 'price-prediction/results',
            element: (
              <Suspense fallback={RESULTS_LOADING_FALLBACK}>
                <PricePredictionResultsPage />
              </Suspense>
            ),
          },
          { path: 'calendar/results', element: withLayout(CalendarResultsPage) },
          { path: 'recommendation', element: <RecommendationProtectedRoute /> },
          {
            path: 'recommendation/loading-preview',
            element: <RecommendationLoadingPreviewPage />,
          },
          {
            path: 'recommendation/results',
            element: (
              <Suspense fallback={RECOMMENDATION_LOADING_FALLBACK}>
                <RecommendationResultsPage />
              </Suspense>
            ),
          },
          { path: 'yield-prediction', element: withLayout(YieldPredictionPage) },
          {
            path: 'yield-prediction/results',
            element: (
              <Suspense fallback={RESULTS_LOADING_FALLBACK}>
                <YieldPredictionResultsPage />
              </Suspense>
            ),
          },
        ],
      },
      { path: '/register', element: <RegisterPage /> },
      {
        path: '/dashboard',
        element: <DashboardShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'notification-center', element: <DashboardNotificationCenterPage /> },
          { path: 'search-management', element: <DashboardSearchManagementPage /> },
          { path: 'system-settings', element: <DashboardSettingsPage /> },
          { path: 'user-management', element: <DashboardUserManagementPage /> },
          { path: 'settings', element: <DashboardSettingsSimplePage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export default appRouter
